import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Loader2, Dumbbell, X, Target, Sparkles, Home } from 'lucide-react';

export default function SelfPlanGenerator({ fr, genForm, setGenForm, focusOptions, generating, onGenerate, onCancel }) {
  return (
    <Card className="bg-card/60 border-border" data-testid="self-plan-generator">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-heading uppercase flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-primary" />
            {fr ? 'Generateur de Plan IA' : 'AI Plan Generator'}
          </h3>
          <Button size="sm" variant="ghost" onClick={onCancel} data-testid="cancel-generator">
            <X className="w-3.5 h-3.5" />
          </Button>
        </div>

        <div>
          <label className="text-[10px] text-muted-foreground">{fr ? 'Focus' : 'Focus'}</label>
          <div className="flex flex-wrap gap-1.5 mt-1">
            {focusOptions.map(f => (
              <Button key={f.v} size="sm" variant={genForm.focus === f.v ? 'default' : 'outline'} onClick={() => setGenForm(g => ({...g, focus: f.v}))} className="text-xs h-7 px-2" data-testid={`self-focus-${f.v}`}>
                {f.l}
              </Button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] text-muted-foreground">{fr ? 'Jours/semaine' : 'Days/week'}</label>
            <div className="flex gap-1 mt-1">
              {[3, 4, 5, 6].map(d => (
                <Button key={d} size="sm" variant={genForm.daysPerWeek === d ? 'default' : 'outline'} onClick={() => setGenForm(g => ({...g, daysPerWeek: d}))} className="flex-1 h-7 text-xs" data-testid={`self-days-${d}`}>
                  {d}
                </Button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-[10px] text-muted-foreground">{fr ? 'Duree (semaines)' : 'Duration (weeks)'}</label>
            <div className="flex gap-1 mt-1">
              {[2, 4, 6, 8].map(w => (
                <Button key={w} size="sm" variant={genForm.weeks === w ? 'default' : 'outline'} onClick={() => setGenForm(g => ({...g, weeks: w}))} className="flex-1 h-7 text-xs" data-testid={`self-weeks-${w}`}>
                  {w}
                </Button>
              ))}
            </div>
          </div>
        </div>

        <div>
          <label className="text-[10px] text-muted-foreground">{fr ? 'Équipement' : 'Equipment'}</label>
          <div className="flex gap-1.5 mt-1">
            <Button size="sm" variant={genForm.equipment === 'gym' ? 'default' : 'outline'} onClick={() => setGenForm(g => ({...g, equipment: 'gym'}))} className="flex-1 text-xs h-7" data-testid="self-equip-gym">
              <Dumbbell className="w-3 h-3 mr-1" /> {fr ? 'Gym' : 'Gym'}
            </Button>
            <Button size="sm" variant={genForm.equipment === 'home' ? 'default' : 'outline'} onClick={() => setGenForm(g => ({...g, equipment: 'home'}))} className="flex-1 text-xs h-7" data-testid="self-equip-home">
              <Home className="w-3 h-3 mr-1" /> {fr ? 'Maison' : 'Home'}
            </Button>
          </div>
        </div>

        <div>
          <label className="text-[10px] text-muted-foreground">{fr ? 'Notes pour l\'IA' : 'Notes for AI'}</label>
          <Input value={genForm.notes} onChange={e => setGenForm(g => ({...g, notes: e.target.value}))} placeholder={fr ? 'Ex: Focus bras, éviter squats...' : 'E.g. Focus arms, avoid squats...'} className="h-8 text-xs mt-1" data-testid="self-notes" />
        </div>

        <div>
          <label className="text-[10px] text-muted-foreground flex items-center gap-1.5">
            <Target className="w-3 h-3 text-amber-400" />
            {fr ? 'Directives du Coach (priorité maximale)' : 'Coach Directives (max priority)'}
          </label>
          <textarea
            value={genForm.coachDirectives || ''}
            onChange={e => setGenForm(g => ({...g, coachDirectives: e.target.value}))}
            placeholder={fr ? 'Ex: Hernie L4-L5, pas de squat libre. Privilegier machines. Mobilité epaule obligatoire.' : 'E.g. L4-L5 herniation, no free squats. Prefer machines. Shoulder mobility mandatory.'}
            rows={3}
            className="w-full mt-1 px-3 py-2 text-xs bg-background border border-amber-500/30 rounded-lg resize-none focus:outline-none focus:ring-1 focus:ring-amber-500/50 placeholder:text-muted-foreground/50"
            data-testid="self-coach-directives"
          />
          <p className="text-[9px] text-amber-400/70 mt-0.5">
            {fr ? 'Ces directives priment sur tout: l\'IA les suivra a la lettre.' : 'These directives override everything: AI will follow them strictly.'}
          </p>
        </div>

        <Button onClick={onGenerate} disabled={generating} className="w-full" data-testid="self-generate-btn">
          {generating ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> {fr ? 'Generation en cours...' : 'Generating...'}</> : <><Sparkles className="w-4 h-4 mr-2" /> {fr ? 'Generer mon plan' : 'Generate my plan'}</>}
        </Button>
      </CardContent>
    </Card>
  );
}
